import { useGetList, useGetOne, useRecordContext } from "ra-core";

import { Skeleton } from "@/components/ui/skeleton";
import { choisirDealEnCours } from "@/fjdigital/diagnostic";
import { fjDealStages } from "@/fjdigital/config";
import { DealDiagnostic } from "../deals/DealDiagnostic";
import type { Company, Contact, Deal } from "../types";

/**
 * Onglet « Diagnostic » de la fiche contact : le diagnostic de la demande en cours.
 * Il est stocké sur le deal (colonne deals.diagnostic), pas sur le contact, pour que
 * chaque demande garde le sien.
 */
export const ContactDiagnostic = () => {
  const contact = useRecordContext<Contact>();

  const {
    data: deals,
    isPending: dealsEnCours,
    error,
  } = useGetList<Deal>(
    "deals",
    {
      filter: { "contact_ids@cs": `{${contact?.id}}` },
      pagination: { page: 1, perPage: 50 },
      sort: { field: "created_at", order: "DESC" },
    },
    { enabled: contact != null },
  );

  const aUneEntreprise = contact?.company_id != null;
  const { data: entreprise, isPending: entrepriseEnCours } = useGetOne<Company>(
    "companies",
    { id: contact?.company_id },
    { enabled: aUneEntreprise },
  );

  if (!contact) return null;

  // La variante (entreprise ou indépendant) est choisie à l'ouverture d'après le secteur :
  // on attend donc l'entreprise avant d'afficher le formulaire.
  if (dealsEnCours || (aUneEntreprise && entrepriseEnCours)) {
    return (
      <div className="flex flex-col gap-3">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-10 w-full" />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <p role="alert" className="py-6 text-center text-destructive">
        Impossible de charger les demandes de ce contact. Recharge la page.
      </p>
    );
  }

  const deal = choisirDealEnCours(deals ?? []);
  if (!deal) {
    return (
      <div className="flex flex-col items-center gap-2 py-8 text-center">
        <p className="font-medium">Pas de demande en cours pour ce contact.</p>
        <p className="max-w-md text-sm text-muted-foreground">
          Le diagnostic se rattache à une demande. Crée une demande dans
          Opportunités, ou attends la prochaine demande du site : le diagnostic
          s'ouvrira ici.
        </p>
      </div>
    );
  }

  const etape = fjDealStages.find((s) => s.value === deal.stage)?.label;
  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm text-muted-foreground">
        Demande :{" "}
        <span className="font-medium text-foreground">{deal.name}</span>
        {etape ? ` (${etape})` : ""}
      </p>
      <DealDiagnostic
        key={deal.id}
        deal={deal}
        secteur={entreprise?.sector}
        contexte={{
          contact: `${contact.first_name} ${contact.last_name}`.trim(),
          entreprise: entreprise?.name,
          formation: deal.name,
        }}
      />
    </div>
  );
};
